/**
 *  @swagger
 *  definitions:
 *    CrewmemberSocialUserParams:
 *      type: object
 *      properties:
 *        Type:
 *          description: Type of the CrewmemberSocialUser
 *          $ref: '#/components/enums/CrewmemberSocialUserType'
 *        Uid:
 *          description: Uid of the CrewmemberSocialUser
 *          type: string
 *        Name:
 *          description: Name of the CrewmemberSocialUser
 *          type: string
 *        Email:
 *          description: Email of the CrewmemberSocialUser
 *          type: string
 *        RemoteImage:
 *          description: Remote Image of the CrewmemberSocialUser
 *          type: string
 *          nullable: true
 *
 *  @swagger
 *  definitions:
 *    CrewmemberSocialUserCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/CrewmemberSocialUserParams'
 *        - type: object
 *          required:
 *            - Type
 *            - Uid
 *            - Name
 *            - Email
 *
 *  @swagger
 *  definitions:
 *    CrewmemberSocialUserQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the CrewmemberSocialUser
 *              type: string
 *        - $ref: '#/definitions/CrewmemberSocialUserParams'
 *
 *  @swagger
 *  definitions:
 *    CrewmemberSocialUserUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/CrewmemberSocialUserQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    CrewmemberSocialUser:
 *      allOf:
 *        - $ref: '#/definitions/CrewmemberSocialUserCreateParams'
 *        - $ref: '#/definitions/CrewmemberSocialUserUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      CrewmemberSocialUser:
 *        allOf:
 *          - $ref: '#/definitions/CrewmemberSocialUser'
 *          - type: object
 *            properties:
 *              Crewmembers:
 *                description: Crewmembers of the CrewmemberSocialUser
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/Crewmember'
 */

// Enums
import {
  CrewmemberSocialUserType,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $Params = {
  Email: string;
  Name: string;
  RemoteImage: null | string;
  Type: CrewmemberSocialUserType;
  Uid: string;
};

export type $CreateParams = $Params;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$Params>;

export type $Associations = {
  Crewmembers?: Array<$Model<'Crewmember'>>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Email: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      isEmail: true,
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  RemoteImage: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Type: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(CrewmemberSocialUserType)),
    validate: {
      notEmpty: true,
    },
  },
  Uid: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'CrewmemberSocialUser'> => {
  const CrewmemberSocialUser = stack.define(
    'CrewmemberSocialUser',
    fields(Sequelize),
    {
      tableName: 'CrewmemberSocialUsers',
    },
  );

  CrewmemberSocialUser.associate = (models: $ModelClasses) => {
    models.CrewmemberSocialUser.hasMany(
      models.Crewmember,
      {
        as: 'Crewmembers',
        foreignKey: 'Email',
        sourceKey: 'Email',
      },
    );
  };

  CrewmemberSocialUser.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return CrewmemberSocialUser;
});
